import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  Slot,
  desktopAndUp,
  tabletAndDown,
} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

export const RequestFailure: FC<{
  className?: string;
  addReloadIcon?: boolean;
  isInWallet?: boolean;
  enableColorErrorIcon?: boolean;
}> = ({className, addReloadIcon, isInWallet, enableColorErrorIcon}) => {
  const {t} = useTranslation();

  return (
    <Slot position={1} columns={3} className={className}>
      <ErrorWrapper $isInWallet={isInWallet} data-testid="walletError">
        <IconWrapper>
          <StyledIcon
            asset={
              enableColorErrorIcon
                ? GenericIconName.ErrorTriangleColor
                : GenericIconName.ErrorTriangle
            }
          />
        </IconWrapper>
        <TextWrapper $isInWallet={isInWallet}>
          <Title $isInWallet={isInWallet}>
            {t('Oops! Something went wrong.')}
          </Title>
          <SubTitle $isInWallet={isInWallet}>
            {t('We couldn’t load this page. Please try again.')}
          </SubTitle>
          <StyledButton
            htmlType={'reset'}
            $isInWallet={isInWallet}
            onClick={() => window.location.reload()}
            aria-label={undefined}
          >
            {addReloadIcon && (
              <Icon
                asset={GenericIconName.Reset}
                size="S"
                fill={COLORS.White}
              />
            )}
            {t('Reload site')}
          </StyledButton>
          <Note $isInWallet={isInWallet}>{t('Reload in 20 seconds ...')}</Note>
        </TextWrapper>
      </ErrorWrapper>
    </Slot>
  );
};

const ErrorWrapper = styled.div<{$isInWallet?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 4rem;

  ${({$isInWallet}) =>
    desktopAndUp(css`
      flex-direction: row;
      gap: 3rem;
      justify-content: center;
      margin-top: 10rem;

      ${$isInWallet &&
      css`
        flex-direction: column;
        margin: 0;
        padding: 87px 16px 0;
        text-align: center;
        gap: 25px;
      `}
    `)}
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TextWrapper = styled.div<{$isInWallet?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  ${tabletAndDown(css`
    align-items: center;
  `)}

  ${({$isInWallet}) =>
    desktopAndUp(css`
      width: 400px;

      ${$isInWallet &&
      css`
        align-items: center;
        width: 100%;
      `}
    `)}
`;

const Title = styled.h3<{$isInWallet?: boolean}>`
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.bold};
  margin: 0;

  ${({$isInWallet}) =>
    $isInWallet &&
    desktopAndUp(css`
      font-weight: ${FONT_WEIGHTS.mediumHeavy};
      line-height: 1.2;
    `)}
`;

const SubTitle = styled.div<{$isInWallet?: boolean}>`
  font-size: ${FONT_SIZES.px18};

  ${$isInWallet =>
    tabletAndDown(css`
      text-align: center;
      padding: 0 40px;

      ${$isInWallet &&
      css`
        font-size: ${FONT_SIZES.px18};
        font-weight: ${FONT_WEIGHTS.mediumBold};
        line-height: 1.4;
      `}
    `)}
`;

const Note = styled.div<{$isInWallet?: boolean}>`
  font-size: ${FONT_SIZES.px12};
  color: ${COLORS.Grey1};

  ${({$isInWallet}) =>
    $isInWallet &&
    desktopAndUp(css`
      line-height: 1.5;
      font-weight: ${FONT_WEIGHTS.mediumBold};
    `)}
`;

const StyledIcon = styled(Icon)<{$isInWallet?: boolean}>`
  height: 110px;
  width: 92px;

  ${({$isInWallet}) =>
    desktopAndUp(css`
      width: 170px;
      height: 170px;

      ${$isInWallet &&
      css`
        width: 109px;
        height: 91px;
      `}
    `)}
`;

const StyledButton = styled(Button)<{$isInWallet?: boolean}>`
  align-items: center;
  display: flex;
  gap: 4px;

  ${({$isInWallet}) =>
    $isInWallet &&
    desktopAndUp(css`
      line-height: 1.4;
    `)}
`;
